"use client";
import { cn } from "@/lib/utils";
import {
  getIrregularOpeningHoursDisplayData,
  getOpeningHoursDisplayData,
} from "@/utils/common";
import Link from "@app/_components/Link";
import { Heading } from "@app/_components/ui";
import useTranslate from "@app/_hooks/useTranslate";
import {
  ExceptionalOpeningHoursDay,
  OpeningHoursDay,
  SideMenuQuery,
} from "@server/gql/graphql";
import { ArrowRight as ArrowIcon } from "lucide-react";
import { ComponentProps } from "react";

export type Menu = NonNullable<SideMenuQuery["sideMenu"]>;
type MenuSection = NonNullable<Menu["navigationsCollection"]>["items"][number];
export type MenuItem = NonNullable<
  NonNullable<MenuSection>["sublinksCollection"]
>["items"][number];

function Store({
  store,
  locale,
}: {
  store: Menu["storeInfo"];
  locale: string;
}) {
  const t = useTranslate();
  if (!store) return null;

  const openingHours = getOpeningHoursDisplayData(
    store.openingHoursCollection?.items as OpeningHoursDay[],
  );
  const irregularOpeningHours = getIrregularOpeningHoursDisplayData(
    store.exceptionalOpeningHoursCollection
      ?.items as ExceptionalOpeningHoursDay[],
    t,
  );
  return (
    <div className="flex flex-col gap-y-4 p-4 text-sm text-gray-300">
      <div className="flex flex-col gap-y-1">
        <Heading className="all-small-caps tracking-wider" as="h3">
          {store.title}
        </Heading>
        {store.email && (
          <a className="hover:underline" href={`mailto:${store.email}`}>
            {store.email}
          </a>
        )}
        {store.phoneNumber && (
          <a className="hover:underline" href={`tel:${store.phoneNumber}`}>
            {store.phoneNumber}
          </a>
        )}
      </div>
      {openingHours?.length ? (
        <div className="flex flex-col gap-y-1">
          <Heading className="all-small-caps tracking-wider" as="h3">
            {t("openingHoursServiceTeam")}
          </Heading>
          {openingHours.map((item) => (
            <p key={item?.text}>{`${item?.text}: ${item?.time}`}</p>
          ))}
        </div>
      ) : null}
      {irregularOpeningHours?.length ? (
        <div className="flex flex-col gap-y-1">
          <Heading className="all-small-caps tracking-wider" as="h3">
            {t("irregularOpeningHours")}
          </Heading>
          {irregularOpeningHours?.map((item) => (
            <p key={item?.text}>{`${item?.text}: ${item?.time}`}</p>
          ))}
        </div>
      ) : null}
      <Link
        locale={locale}
        className="group inline-flex items-center underline"
        href={store?.storePage?.slug}
      >
        {store?.storePageLinkText}
        <ArrowIcon
          strokeWidth={1}
          className="size-4.5 text-black opacity-0 transition-[opacity,transform] group-hover:translate-x-0.5 group-hover:opacity-100 supports-[hover]:opacity-0"
        />
      </Link>
    </div>
  );
}

function MenuItem({
  link,
  locale,
  className,
  activeMenuItem,
  ...props
}: ComponentProps<"li"> & {
  link: MenuItem;
  locale: string;
  activeMenuItem?: MenuItem | null;
}) {
  return (
    <li className={cn("flex flex-col", className)} {...props}>
      <Link
        href={link?.internalLink?.slug ?? "/"}
        locale={locale}
        data-active={activeMenuItem?.sys?.id === link?.sys?.id}
        className="px-4 py-1 data-active:underline"
      >
        {link?.title}
      </Link>
      {link?.sublinksCollection?.items.length ? (
        <MenuSection
          key={link?.sys?.id}
          section={link}
          locale={locale}
          className="ml-4"
          activeMenuItem={activeMenuItem}
        />
      ) : null}
    </li>
  );
}

function MenuSection({
  section,
  locale,
  className,
  activeMenuItem,
  ...props
}: ComponentProps<"ul"> & {
  section: MenuSection | MenuItem;
  locale: string;
  activeMenuItem?: MenuItem | null;
}) {
  return (
    <ul className={cn("flex flex-col", className)} {...props}>
      {section?.sublinksCollection?.items.map((item) => (
        <MenuItem
          key={item?.sys?.id}
          link={item}
          locale={locale}
          activeMenuItem={activeMenuItem}
        />
      ))}
    </ul>
  );
}

export default function Menu({
  menu,
  locale,
  className,
  activeMenuItem,
  ...props
}: ComponentProps<"nav"> & {
  menu?: Menu | null;
  locale: string;
  activeMenuItem?: MenuItem | null;
}) {
  if (!menu) return null;
  return (
    <nav className={cn("flex flex-col", className)} {...props}>
      {menu.navigationsCollection?.items?.map((section) => (
        <div
          key={section?.sys.id}
          className="flex flex-col gap-y-2 border-b py-4"
        >
          <h3 className="all-small-caps px-4 text-lg tracking-wider uppercase">
            {section?.title}
          </h3>
          <MenuSection
            key={section?.sys?.id}
            section={section}
            locale={locale}
            activeMenuItem={activeMenuItem}
          />
        </div>
      ))}
      {menu.storeInfo && <Store store={menu.storeInfo} locale={locale} />}
    </nav>
  );
}
